// SKU enable/disable UI logic — the confirmation-modal copy + the optimistic
// cache transform. Pure so both are unit-testable without rendering.

import type { InventoryListResponse } from '@gutterinv/shared';

export interface StatusConfirmCopy {
  title: string;
  description: string;
  confirmLabel: string;
  variant: 'default' | 'destructive';
}

/**
 * Confirmation-modal copy for an enable/disable. `active` is the TARGET state.
 * `label` (e.g. "EC5ALM – 5\" End Cap, Almond") is set for a single-SKU change;
 * omit it for a bulk change (uses the count). Disable is the higher-consequence
 * direction → destructive variant.
 */
export function statusConfirmCopy(req: {
  active: boolean;
  count: number;
  label?: string;
}): StatusConfirmCopy {
  const { active, count, label } = req;
  const one = count === 1;
  const them = one ? 'it' : 'them';
  if (active) {
    return {
      title: one ? 'Enable this SKU?' : `Enable ${count} SKUs?`,
      description: label
        ? `“${label}” will return to active inventory.`
        : `${count} SKU${one ? '' : 's'} will return to active inventory.`,
      confirmLabel: one ? 'Enable' : `Enable ${count}`,
      variant: 'default',
    };
  }
  return {
    title: one ? 'Disable this SKU?' : `Disable ${count} SKUs?`,
    description: label
      ? `“${label}” will be hidden from active inventory. You can re-enable it anytime.`
      : `${count} SKU${one ? '' : 's'} will be hidden from active inventory. You can re-enable ${them} anytime.`,
    confirmLabel: one ? 'Disable' : `Disable ${count}`,
    variant: 'destructive',
  };
}

/**
 * Optimistic update for the inventory list cache: flip `active` on the rows
 * whose skuId is in `ids`, leaving every other row + field untouched. Drives
 * the status badge + the Active/Disabled filter counts (both derived from this
 * list) before the server responds.
 */
export function applyActiveOptimistic(
  prev: InventoryListResponse,
  ids: readonly string[],
  active: boolean,
): InventoryListResponse {
  const idSet = new Set(ids);
  return {
    ...prev,
    skus: prev.skus.map((row) => (idSet.has(row.skuId) ? { ...row, active } : row)),
  };
}
